import { useEffect, useMemo, useState } from "react";
import {
  getRequiredCarePassPlanIdsForShift,
  preferredHourlyPackIdForVisits,
  preferredShortTermPackIdForDays,
  resolveMinimumCoveringHourlyPack,
} from "../../config/careShiftWindows";
import {
  PRICING_PLANS,
  SHORT_TERM_OPTIONS,
  HOURLY_PACK_OPTIONS,
  applyBackendPricingPlans,
  formatInr,
  shortTermPackDaysLabel,
} from "../public/pricingPlansConfig";
import SorryMessageModal from "../shared/SorryMessageModal";
import api from "../../services/api";
import "./BookingCarePassStep.css";

const MULTI_PLAN_MESSAGE =
  "We're very sorry — you cannot select two Care Passes at the same time. Please choose either a Booking Pack or a Short-term Care Pass. Tap Switch Plan to change your selection, or close to keep the current one.";


function CheckIcon({ tone = "green" }) {
  return (
    <svg className={`bcps-check bcps-check--${tone}`} viewBox="0 0 20 20" fill="none" aria-hidden>
      <circle cx="10" cy="10" r="10" fill="currentColor" />
      <path d="M6.2 10.3 8.6 12.7 14 7.3" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ShortTermCard({ plan, onChoose, busy, selectedId, onSelect }) {
  const options = plan?.selector?.options?.length ? plan.selector.options : SHORT_TERM_OPTIONS;
  const defaultId = plan?.selector?.defaultId || options[0]?.id || "3";
  const [localSelectedId, setLocalSelectedId] = useState(String(defaultId));
  const isControlled = typeof onSelect === "function";
  const activeId = isControlled ? selectedId : localSelectedId;
  const selected =
    options.find((o) => String(o.id) === String(activeId)) ||
    (isControlled ? null : options[0]) ||
    options.find((o) => String(o.id) === String(defaultId)) ||
    options[0];
  const daysLabel = shortTermPackDaysLabel(selected) || selected?.label || "";

  function handleSelect(id) {
    if (isControlled) onSelect(String(id));
    else setLocalSelectedId(String(id));
  }

  return (
    <article className="bcps-card">
      <header className="bcps-card__head">
        <span className="bcps-card__eyebrow">TEMPORARY RECOVERY</span>
        <h3 className="bcps-card__title">{plan?.title || "NEMA SHORT-TERM CARE PASSES"}</h3>
        <p className="bcps-card__subtitle">
          {plan?.subtitle || "One-Time Marketplace Pass · Start small, upgrade anytime"}
        </p>
        <p className="bcps-card__price-label">{selected?.name || "Care Pass"}</p>
        <p className="bcps-card__price">
          <span>₹{formatInr(selected?.price || 500)}</span>
          <small>{daysLabel}</small>
        </p>
        <p className="bcps-card__note">
          {plan?.priceNote || "No recurring subscription · Upgrade by paying only the difference"}
        </p>
      </header>

      <div className="bcps-card__body">
        <p className="bcps-card__section">Choose your Care Pass</p>
        <p className="bcps-card__hint">
          Each plan gives you that many caregiver days. Book them together or on different dates — for
          example tomorrow and next month — until your days are used.
        </p>
        <ul className="bcps-duration" role="radiogroup" aria-label="Short-term Care Pass">
          {options.map((opt) => {
            const active = activeId != null && String(opt.id) === String(activeId);
            return (
              <li key={opt.id}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={active}
                  className={`bcps-duration__opt${active ? " is-selected" : ""}`}
                  onClick={() => handleSelect(opt.id)}
                  disabled={busy}
                >
                  <span className="bcps-duration__radio" aria-hidden />
                  <span className="bcps-duration__label">{opt.label || opt.name}</span>
                  <span className="bcps-duration__price">₹{formatInr(opt.price)}</span>
                </button>
              </li>
            );
          })}
        </ul>

        <p className="bcps-card__upgrade-note">
          Need more time later? Upgrade to a longer pass within 72 hours and pay only the price difference — your first payment is never wasted.
        </p>

        <ul className="bcps-features">
          {(plan?.included || []).map((item) => (
            <li key={item}>
              <CheckIcon tone="green" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>

      <button
        type="button"
        className="bcps-card__cta bcps-card__cta--primary"
        disabled={busy}
        onClick={() =>
          onChoose?.({
            planId: "short",
            pack: selected?.id || defaultId || options[0]?.id,
          })
        }
      >
        {plan?.cta || "CHOOSE SHORT-TERM PASS"}
      </button>
    </article>
  );
}

function QuarterlyCard({ plan, onChoose, busy }) {
  const price = Number(plan?.price || 3000);

  return (
    <article className="bcps-card bcps-card--featured">
      <span className="bcps-card__popular">★ MOST POPULAR</span>
      <header className="bcps-card__head">
        <span className="bcps-card__eyebrow">CONTINUOUS CARE</span>
        <h3 className="bcps-card__title">{plan?.title || "NEMA QUARTERLY CARE PASS"}</h3>
        <p className="bcps-card__subtitle">
          {plan?.subtitle || "3 months of continuous care access"}
        </p>
        <p className="bcps-card__price-label">Care Pass price</p>
        <p className="bcps-card__price">
          <span>₹{formatInr(price)}</span>
          <small>3 months continuous</small>
        </p>
        <p className="bcps-card__equiv">Equivalent to just ₹{formatInr(Math.round(price / 3))} per month</p>
      </header>

      <div className="bcps-card__body">
        <p className="bcps-card__hint">
          Best for ongoing care — book caregivers anytime throughout the full 3-month period.
          This is continuous access, not a limited day pack.
        </p>
        {plan?.launchBenefits ? (
          <div className="bcps-launch">
            <strong>{plan.launchBenefits.title}</strong>
            <ul>
              {(plan.launchBenefits.items || []).map((item) => (
                <li key={item}>
                  <CheckIcon tone="green" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <p className="bcps-card__section">{plan?.includedTitle || "What's included"}</p>
        <ul className="bcps-features">
          {(plan?.included || []).map((item) => (
            <li key={item}>
              <CheckIcon tone="blue" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>

      <button
        type="button"
        className="bcps-card__cta bcps-card__cta--solid"
        disabled={busy}
        onClick={() => onChoose?.({ planId: "long" })}
      >
        {plan?.cta || "START QUARTERLY CARE PASS"}
      </button>
    </article>
  );
}

function HourlyCard({
  plan,
  onChoose,
  busy,
  selectedId,
  onSelect,
  compact = false,
  recommendedId = null,
  requiredBookings = null,
}) {
  const options = plan?.selector?.options?.length ? plan.selector.options : [];
  const defaultId = plan?.selector?.defaultId || options[0]?.id || "1";
  const [localSelectedId, setLocalSelectedId] = useState(String(defaultId));
  const isControlled = typeof onSelect === "function";
  const activeId = isControlled ? selectedId : localSelectedId;
  const selected =
    options.find((o) => String(o.id) === String(activeId)) ||
    (isControlled ? null : options[0]) ||
    options.find((o) => String(o.id) === String(defaultId)) ||
    options[0];
  const need = Math.max(1, Math.floor(Number(requiredBookings)) || 1);

  function handleSelect(id) {
    if (isControlled) onSelect(String(id));
    else setLocalSelectedId(String(id));
  }

  const priceLabel = `₹${formatInr(selected?.price || plan?.price || 100)}`;
  const ctaLabel = compact
    ? `Continue · ${priceLabel}`
    : plan?.cta || "GET HOURLY PASS";

  return (
    <article
      className={`bcps-card bcps-card--hourly${compact ? " bcps-card--compact" : " bcps-card--featured"}`}
    >
      <header className="bcps-card__head bcps-card__head--slim">
        <div className="bcps-card__head-main">
          <span className="bcps-card__eyebrow">Hourly visits</span>
          <h3 className="bcps-card__title">{plan?.title || "NEMA Hourly Care Pass"}</h3>
          <p className="bcps-card__subtitle">
            {plan?.subtitle || "Under 2h sessions · Caregiver fee paid separately"}
          </p>
        </div>
        <p className="bcps-card__price bcps-card__price--inline" aria-live="polite">
          <span>{priceLabel}</span>
          <small>{selected?.label || "Selected pack"}</small>
        </p>
      </header>

      <div className="bcps-card__body">
        {options.length ? (
          <>
            <p className="bcps-card__section">Choose pack</p>
            <ul className="bcps-duration" role="radiogroup" aria-label="Hourly Care Pass">
              {options.map((opt) => {
                const active = activeId != null && String(opt.id) === String(activeId);
                const recommended =
                  recommendedId != null && String(opt.id) === String(recommendedId);
                const packSize = Number(opt.bookings != null ? opt.bookings : opt.id);
                const covers = Number.isFinite(packSize) && packSize >= need;
                return (
                  <li key={opt.id}>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={active}
                      className={`bcps-duration__opt${active ? " is-selected" : ""}${
                        recommended ? " is-recommended" : ""
                      }${requiredBookings != null && !covers ? " is-insufficient" : ""}`}
                      onClick={() => handleSelect(opt.id)}
                      disabled={busy || (requiredBookings != null && !covers)}
                    >
                      <span className="bcps-duration__radio" aria-hidden />
                      <span className="bcps-duration__label">
                        {opt.label || opt.name}
                        {recommended ? (
                          <span className="bcps-duration__badge">Recommended</span>
                        ) : null}
                      </span>
                      <span className="bcps-duration__price">₹{formatInr(opt.price)}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        ) : null}
        {!compact ? (
          <ul className="bcps-features bcps-features--compact">
            {(plan?.included || [
              "Unlocks hourly Care Attendant bookings",
              "Valid for the selected booking pack",
            ])
              .slice(0, 2)
              .map((item) => (
                <li key={item}>
                  <CheckIcon tone="blue" />
                  <span>{item}</span>
                </li>
              ))}
          </ul>
        ) : null}
      </div>

      <button
        type="button"
        className="bcps-card__cta bcps-card__cta--solid"
        disabled={busy}
        onClick={() => onChoose?.({ planId: "hourly", pack: selected?.id })}
      >
        {ctaLabel}
      </button>
    </article>
  );
}

/**
 * Compact Care Pass picker for booking flow when patient pass is inactive / incompatible.
 */
export default function BookingCarePassStep({
  patientName = "",
  searchShift = "",
  bookingDays = null,
  onBack,
  onChoosePlan,
  busy = false,
  compact = false,
}) {
  const requiredPlanIds = useMemo(
    () => getRequiredCarePassPlanIdsForShift(searchShift, { bookingDays }),
    [searchShift, bookingDays]
  );
  const [plans, setPlans] = useState(() =>
    PRICING_PLANS.filter((p) => requiredPlanIds.includes(p.id))
  );
  /** Mutual exclusion: only one plan card may show a selected pack at a time. */
  const [activeSelection, setActiveSelection] = useState(null);
  const [pendingSelection, setPendingSelection] = useState(null);
  const [multiPlanModalOpen, setMultiPlanModalOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const need =
          bookingDays != null && Number.isFinite(Number(bookingDays)) && Number(bookingDays) > 0
            ? Math.floor(Number(bookingDays))
            : null;
        const res = await api.getCarePassPlans?.(need != null ? { requiredBookings: need } : {});
        const backend = res?.plans || res?.data?.plans || res;
        if (cancelled || !backend) return;
        const merged = applyBackendPricingPlans(PRICING_PLANS, backend).filter((p) =>
          requiredPlanIds.includes(p.id)
        );
        if (merged.length) setPlans(merged);
        else setPlans(PRICING_PLANS.filter((p) => requiredPlanIds.includes(p.id)));
      } catch {
        setPlans(PRICING_PLANS.filter((p) => requiredPlanIds.includes(p.id)));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [requiredPlanIds, bookingDays]);

  const shortPlan = useMemo(() => plans.find((p) => p.id === "short"), [plans]);
  const longPlan = useMemo(() => plans.find((p) => p.id === "long"), [plans]);
  const hourlyPlan = useMemo(() => plans.find((p) => p.id === "hourly"), [plans]);
  const hourlyOptions = useMemo(
    () =>
      hourlyPlan?.selector?.options?.length
        ? hourlyPlan.selector.options
        : HOURLY_PACK_OPTIONS,
    [hourlyPlan]
  );
  const recommendedHourlyPack = useMemo(
    () => resolveMinimumCoveringHourlyPack(bookingDays, hourlyOptions),
    [bookingDays, hourlyOptions]
  );

  useEffect(() => {
    if (hourlyPlan && requiredPlanIds.includes("hourly") && requiredPlanIds.length === 1) {
      const def = preferredHourlyPackIdForVisits(bookingDays, hourlyOptions);
      setActiveSelection((prev) => {
        const need = Math.max(1, Math.floor(Number(bookingDays)) || 1);
        if (prev?.planId === "hourly") {
          const prevSize = Number(prev.packId);
          // Keep a manually chosen pack only if it still covers the requirement.
          if (Number.isFinite(prevSize) && prevSize >= need && String(prev.packId) !== String(def)) {
            return prev;
          }
          if (String(prev.packId) === String(def)) return prev;
        }
        return { planId: "hourly", packId: String(def) };
      });
      return;
    }
    if (shortPlan && requiredPlanIds.includes("short")) {
      const options = shortPlan?.selector?.options?.length
        ? shortPlan.selector.options
        : SHORT_TERM_OPTIONS;
      const def = preferredShortTermPackIdForDays(bookingDays, options);
      setActiveSelection((prev) => {
        if (prev?.planId === "short" && String(prev.packId) === String(def)) return prev;
        return { planId: "short", packId: String(def) };
      });
    }
  }, [hourlyPlan, shortPlan, requiredPlanIds, bookingDays, hourlyOptions]);

  const notice = useMemo(() => {
    const who = patientName ? ` for ${patientName}` : "";
    const days = Number(bookingDays);
    const needsQuarterly = Number.isFinite(days) && days > 30;
    if (requiredPlanIds.includes("hourly") && requiredPlanIds.length === 1) {
      if (Number.isFinite(days) && days > 1) {
        return `You have selected ${days} recurring booking${days === 1 ? "" : "s"}${who}. Please select a Care Pass that covers your booking requirement.`;
      }
      return `Hourly booking requires an Hourly Pass${who}. Choose a pack to continue.`;
    }
    if (needsQuarterly || (requiredPlanIds.includes("long") && !requiredPlanIds.includes("short"))) {
      return `Continuous care longer than one month requires a Quarterly Care Pass${who} — 3 months of continuous access.`;
    }
    if (
      requiredPlanIds.includes("short") &&
      requiredPlanIds.includes("long") &&
      !requiredPlanIds.includes("hourly")
    ) {
      if (Number.isFinite(days) && days > 0) {
        return `This booking needs ${days} caregiver day${days === 1 ? "" : "s"}${who}. Choose a Short-term pack that covers them, or a Quarterly Care Pass for continuous care.`;
      }
      return `Choose a Short-term Care Pass (up to 1 month of care days) or Quarterly Care Pass (continuous care beyond 1 month)${who} to continue booking.`;
    }
    return `No applicable Care Pass${who}. Choose a plan to unlock booking, then confirm your caregiver.`;
  }, [patientName, requiredPlanIds, bookingDays]);

  function handleSelectPack(planId, packId) {
    const next = { planId, packId: String(packId) };
    if (activeSelection && activeSelection.planId !== planId) {
      setPendingSelection(next);
      setMultiPlanModalOpen(true);
      return;
    }
    setActiveSelection(next);
  }

  function closeMultiPlanModal() {
    setPendingSelection(null);
    setMultiPlanModalOpen(false);
  }

  function confirmSwitchPlan() {
    if (pendingSelection) setActiveSelection(pendingSelection);
    closeMultiPlanModal();
  }

  const hourlyOnly =
    requiredPlanIds.includes("hourly") && requiredPlanIds.length === 1;
  const denseHourlyCard = compact || hourlyOnly;

  return (
    <div className={`bcps${busy ? " is-busy" : ""}${compact ? " bcps--compact" : ""}${hourlyOnly ? " bcps--hourly-only" : ""}`}>
      <SorryMessageModal
        open={multiPlanModalOpen}
        message={MULTI_PLAN_MESSAGE}
        actionLabel="Switch Plan"
        onClose={closeMultiPlanModal}
        onAction={confirmSwitchPlan}
      />
      {!compact ? (
        <div className="bcps__head">
          {onBack ? (
            <button type="button" className="bcps__back" onClick={onBack} disabled={busy}>
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.25">
                <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>Back</span>
            </button>
          ) : null}
          <div className="bcps__head-copy">
            <h2 className="bcps__title">
              {hourlyOnly ? "Choose an Hourly Pass" : "Choose a Care Pass"}
            </h2>
            <p className="bcps__sub">{notice}</p>
          </div>
        </div>
      ) : null}

      {recommendedHourlyPack && Number(bookingDays) > 0 ? (
        <p className="bcps__recommend" role="status">
          Recommended:{" "}
          <strong>
            {recommendedHourlyPack.label} · ₹{formatInr(recommendedHourlyPack.price)}
          </strong>
          {!recommendedHourlyPack.coversExactly
            ? ` · covers ${recommendedHourlyPack.requiredBookings} booking${
                recommendedHourlyPack.requiredBookings === 1 ? "" : "s"
              }`
            : null}
        </p>
      ) : null}

      <div className="bcps__grid">
        {hourlyPlan ? (
          <HourlyCard
            plan={hourlyPlan}
            onChoose={onChoosePlan}
            busy={busy}
            compact={denseHourlyCard}
            selectedId={activeSelection?.planId === "hourly" ? activeSelection.packId : null}
            onSelect={(packId) => handleSelectPack("hourly", packId)}
            recommendedId={recommendedHourlyPack?.id || null}
            requiredBookings={bookingDays}
          />
        ) : null}
        {shortPlan ? (
          <ShortTermCard
            plan={shortPlan}
            onChoose={onChoosePlan}
            busy={busy}
            selectedId={activeSelection?.planId === "short" ? activeSelection.packId : null}
            onSelect={(packId) => handleSelectPack("short", packId)}
          />
        ) : null}
        {longPlan ? <QuarterlyCard plan={longPlan} onChoose={onChoosePlan} busy={busy} /> : null}
      </div>
    </div>
  );
}
